import React, { useState, useEffect } from 'react';
import { ProtectedRoute } from '../auth/ProtectedRoute';
import { useAuth } from '../auth/useAuth';
import { AVAILABLE_CITIES } from '../utils/cities';
import './AdminUsersPage.css';

interface User {
  id: string;
  username: string;
  fullName: string;
  phone: string;
  city: string | string[]; // Может быть строкой (для совместимости) или массивом
  role: 'admin' | 'tester';
}

const AdminUsersPage: React.FC = () => {
  const [users, setUsers] = useState<User[]>([]);
  const [showForm, setShowForm] = useState(false);
  const [editingUser, setEditingUser] = useState<User | null>(null);
  const [formData, setFormData] = useState({
    username: '',
    password: '',
    fullName: '',
    phone: '',
    city: [] as string[],
    role: 'tester' as 'admin' | 'tester'
  });
  const { isAdmin, logout } = useAuth();
  const backend = process.env.REACT_APP_BACKEND_URL || '';

  useEffect(() => {
    if (isAdmin) {
      loadUsers();
    }
  }, [isAdmin]);

  const loadUsers = async () => {
    try {
      const token = localStorage.getItem('token');
      if (!token) {
        console.error('No token found in localStorage');
        logout();
        return;
      }
      
      console.log('Loading users with token:', token.substring(0, 20) + '...');
      const response = await fetch(`${backend}/admin/users`, {
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        }
      });
      
      console.log('Load users response:', response.status, response.statusText);
      
      if (response.ok) {
        const data = await response.json();
        setUsers(data.users || []);
      } else if (response.status === 401 || response.status === 403) {
        console.error('Auth error:', response.status);
        const errorData = await response.json().catch(() => ({}));
        console.error('Error details:', errorData);
        logout();
      } else {
        const errorData = await response.json().catch(() => ({}));
        console.error('Unexpected error:', response.status, errorData);
      }
    } catch (error) {
      console.error('Error loading users:', error);
    }
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    
    // Валидация: проверяем что выбран хотя бы один город
    if (formData.city.length === 0) {
      alert('Пожалуйста, выберите хотя бы один город');
      return;
    }
    
    try {
      const token = localStorage.getItem('token');
      if (!token) {
        alert('Сессия истекла. Пожалуйста, войдите снова.');
        logout();
        return;
      }
      
      const url = editingUser 
        ? `${backend}/admin/users/${editingUser.id}`
        : `${backend}/admin/users`;
      
      const method = editingUser ? 'PUT' : 'POST';
      const body = editingUser 
        ? { ...formData, password: formData.password || undefined }
        : formData;

      console.log('Saving user:', { method, url, body: { ...body, password: '***' } });
      
      const response = await fetch(url, {
        method,
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${token}`
        },
        body: JSON.stringify(body)
      });
      
      console.log('Save response:', response.status, response.statusText);

      if (response.ok) {
        await loadUsers();
        resetForm();
      } else {
        let errorMessage = 'Ошибка при сохранении пользователя';
        try {
          const errorData = await response.json();
          errorMessage = errorData.message || errorMessage;
        } catch (e) {
          errorMessage = `HTTP ${response.status}: ${response.statusText}`;
        }
        console.error('Save user error:', response.status, errorMessage);
        alert(errorMessage);
        
        if (response.status === 401 || response.status === 403) {
          logout();
        }
      }
    } catch (error) {
      console.error('Error saving user:', error);
      alert('Ошибка при сохранении пользователя');
    }
  };

  const handleDelete = async (id: string) => {
    if (!window.confirm('Вы уверены, что хотите удалить этого пользователя?')) {
      return;
    }

    try {
      const token = localStorage.getItem('token');
      const response = await fetch(`${backend}/admin/users/${id}`, {
        method: 'DELETE',
        headers: {
          'Authorization': `Bearer ${token}`
        }
      });

      if (response.ok) {
        await loadUsers();
      } else {
        let errorMessage = 'Ошибка при удалении пользователя';
        try {
          const errorData = await response.json();
          errorMessage = errorData.message || errorMessage;
        } catch (e) {
          errorMessage = `HTTP ${response.status}: ${response.statusText}`;
        }
        console.error('Delete user error:', response.status, errorMessage);
        alert(errorMessage);
        
        if (response.status === 401 || response.status === 403) {
          logout();
        }
      }
    } catch (error) {
      console.error('Error deleting user:', error);
      alert('Ошибка при удалении пользователя');
    }
  };

  const resetForm = () => {
    setFormData({
      username: '',
      password: '',
      fullName: '',
      phone: '',
      city: [],
      role: 'tester'
    });
    setEditingUser(null);
    setShowForm(false);
  };

  const startEdit = (user: User) => {
    setEditingUser(user);
    // Преобразуем city в массив если это строка
    const cityArray = Array.isArray(user.city) ? user.city : (user.city ? [user.city] : []);
    setFormData({
      username: user.username,
      password: '',
      fullName: user.fullName,
      phone: user.phone,
      city: cityArray,
      role: user.role
    });
    setShowForm(true);
  };

  const handleCityChange = (e: React.ChangeEvent<HTMLSelectElement>) => {
    const selectedOptions = Array.from(e.target.selectedOptions, option => option.value);
    setFormData({ ...formData, city: selectedOptions });
  };

  return (
    <ProtectedRoute requireAdmin>
      <div className="admin-users-page">
        <div className="page-header">
          <div>
            <h1>Управление пользователями</h1>
            <div style={{ display: 'flex', gap: '10px', marginTop: '10px' }}>
              <button 
                className="secondary-button" 
                onClick={() => window.location.href = '/'}
              >
                На главную
              </button>
              <button 
                className="secondary-button" 
                onClick={logout}
                style={{
                  backgroundColor: '#dc3545',
                  color: '#ffffff',
                  borderColor: '#dc3545'
                }}
                onMouseEnter={(e) => {
                  e.currentTarget.style.backgroundColor = '#c82333';
                  e.currentTarget.style.borderColor = '#c82333';
                }}
                onMouseLeave={(e) => {
                  e.currentTarget.style.backgroundColor = '#dc3545';
                  e.currentTarget.style.borderColor = '#dc3545';
                }}
              >
                Выйти
              </button>
            </div>
          </div>
          <button className="primary-button" onClick={() => setShowForm(true)}>
            + Добавить пользователя
          </button>
        </div>

        {showForm && (
          <div className="user-form-modal">
            <div className="modal-content">
              <h2>{editingUser ? 'Редактировать пользователя' : 'Новый пользователь'}</h2>
              <form onSubmit={handleSubmit}>
                <div className="form-group">
                  <label>Логин:</label>
                  <input
                    type="text"
                    value={formData.username}
                    onChange={(e) => setFormData({ ...formData, username: e.target.value })}
                    required
                    disabled={!!editingUser}
                  />
                </div>
                <div className="form-group">
                  <label>Пароль {editingUser && '(оставьте пустым, чтобы не менять)'}:</label>
                  <input
                    type="password"
                    value={formData.password}
                    onChange={(e) => setFormData({ ...formData, password: e.target.value })}
                    required={!editingUser}
                  />
                </div>
                <div className="form-group">
                  <label>ФИО:</label>
                  <input
                    type="text"
                    value={formData.fullName}
                    onChange={(e) => setFormData({ ...formData, fullName: e.target.value })}
                    required
                  />
                </div>
                <div className="form-group">
                  <label>Номер телефона:</label>
                  <input
                    type="tel"
                    value={formData.phone}
                    onChange={(e) => setFormData({ ...formData, phone: e.target.value })}
                    required
                  />
                </div>
                <div className="form-group">
                  <label>Города (выберите несколько, удерживая Ctrl или Cmd):</label>
                  <select
                    multiple
                    value={formData.city}
                    onChange={handleCityChange}
                    required
                    size={Math.min(6, AVAILABLE_CITIES.length)}
                    className="city-multiselect"
                  >
                    {AVAILABLE_CITIES.map(city => (
                      <option key={city} value={city}>
                        {city}
                      </option>
                    ))}
                  </select>
                  <div style={{ 
                    marginTop: '8px', 
                    fontSize: '13px', 
                    color: '#666',
                    fontStyle: 'italic'
                  }}>
                    {formData.city.length > 0 
                      ? `Выбрано городов: ${formData.city.length} (${formData.city.join(', ')})`
                      : 'Удерживайте Ctrl (Cmd на Mac) для выбора нескольких городов'}
                  </div>
                </div>
                <div className="form-group">
                  <label>Роль:</label>
                  <select
                    value={formData.role}
                    onChange={(e) => setFormData({ ...formData, role: e.target.value as 'admin' | 'tester' })}
                  >
                    <option value="tester">Тестер</option>
                    <option value="admin">Админ</option>
                  </select>
                </div>
                <div className="form-actions">
                  <button type="submit" className="primary-button">
                    Сохранить
                  </button>
                  <button type="button" className="secondary-button" onClick={resetForm}>
                    Отмена
                  </button>
                </div>
              </form>
            </div>
          </div>
        )}

        <div className="users-table-container">
          <table className="users-table">
            <thead>
              <tr>
                <th>ID</th>
                <th>Логин</th>
                <th>ФИО</th>
                <th>Телефон</th>
                <th>Города</th>
                <th>Роль</th>
                <th>Действия</th>
              </tr>
            </thead>
            <tbody>
              {users.length === 0 ? (
                <tr>
                  <td colSpan={7} style={{ textAlign: 'center', padding: '20px' }}>
                    Пользователи не найдены
                  </td>
                </tr>
              ) : (
                users.map((user) => (
                  <tr key={user.id}>
                    <td>{user.id}</td>
                    <td>{user.username}</td>
                    <td>{user.fullName}</td>
                    <td>{user.phone}</td>
                    <td>
                      {Array.isArray(user.city) 
                        ? user.city.join(', ') 
                        : user.city || '-'}
                    </td>
                    <td>{user.role === 'admin' ? 'Админ' : 'Тестер'}</td>
                    <td>
                      <button 
                        className="edit-button"
                        onClick={() => startEdit(user)}
                      >
                        Редактировать
                      </button>
                      <button 
                        className="delete-button"
                        onClick={() => handleDelete(user.id)}
                      >
                        Удалить
                      </button>
                    </td>
                  </tr>
                ))
              )}
            </tbody>
          </table>
        </div>
      </div>
    </ProtectedRoute>
  );
};

export default AdminUsersPage;

